import { useState, type CSSProperties } from 'react';
import {
  useCpcAllRoleRates,
  useCreateRoleRate,
  useUpdateRoleRate,
  useDeleteRoleRate,
  useCpcActiveFactorSet,
} from './cpc-hooks';
import type { CpcRoleRate, CreateRoleRatePayload, UpdateRoleRatePayload } from './cpc-types';
import { Modal } from '../components/Modal';

// ── Konstanten ────────────────────────────────────────────────────────────────

const SEGMENT_LABELS: Record<string, string> = {
  standard: 'Standard',
  enterprise: 'Enterprise',
  public_sector: 'Öffentl. Sektor',
};

// ── Styles ────────────────────────────────────────────────────────────────────

const thStyle: CSSProperties = {
  padding: '0.625rem 1rem', textAlign: 'left', fontSize: '0.75rem',
  textTransform: 'uppercase', color: 'var(--color-text-muted)',
  fontWeight: 600, whiteSpace: 'nowrap',
};

const tdStyle: CSSProperties = {
  padding: '0.75rem 1rem', fontSize: '0.875rem', verticalAlign: 'middle',
};

const btnStyle = (variant: 'primary' | 'default' | 'danger'): CSSProperties => {
  if (variant === 'primary') return {
    padding: '0.5rem 1rem', borderRadius: '0.375rem', border: 'none',
    background: 'var(--color-primary, #3B82F6)', color: '#fff',
    fontSize: '0.8125rem', fontWeight: 500, cursor: 'pointer',
  };
  if (variant === 'danger') return {
    padding: '0.3rem 0.625rem', borderRadius: '0.25rem',
    border: '1px solid #fecaca', background: '#fef2f2',
    fontSize: '0.75rem', cursor: 'pointer', color: '#dc2626',
  };
  return {
    padding: '0.3rem 0.625rem', borderRadius: '0.25rem',
    border: '1px solid var(--color-border)', background: 'var(--color-bg-card)',
    fontSize: '0.75rem', cursor: 'pointer', color: 'var(--color-text)',
  };
};

// ── Helper ────────────────────────────────────────────────────────────────────

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function fmtRate(dec: string): string {
  return Number(dec).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
}

function isExpired(rate: CpcRoleRate): boolean {
  if (!rate.effectiveTo) return false;
  return new Date(rate.effectiveTo) < new Date();
}

// ── Formular-Zustand ──────────────────────────────────────────────────────────

interface RateForm {
  role: string;
  dailyRateEur: string;
  effectiveFrom: string;
  effectiveTo: string;
  clientSegment: string;
}

const emptyForm = (): RateForm => ({
  role: '',
  dailyRateEur: '',
  effectiveFrom: new Date().toISOString().split('T')[0],
  effectiveTo: '',
  clientSegment: '',
});

// ── Haupt-Komponente ──────────────────────────────────────────────────────────

export function RateAdminTab() {
  const { data: rates = [], isLoading } = useCpcAllRoleRates();
  const { data: activeFactorSet } = useCpcActiveFactorSet();
  const createMutation = useCreateRoleRate();
  const updateMutation = useUpdateRoleRate();
  const deleteMutation = useDeleteRoleRate();

  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<RateForm>(emptyForm());
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [error, setError] = useState('');

  // Rollen aus aktivem Faktor-Set für Dropdown
  const availableRoles = (activeFactorSet as any)?.payload?.roles as Array<{ key: string; label: string }> | undefined;

  function openCreate() {
    setEditingId(null);
    setForm(emptyForm());
    setError('');
    setModalOpen(true);
  }

  function openEdit(rate: CpcRoleRate) {
    setEditingId(rate.id);
    setForm({
      role: rate.role,
      dailyRateEur: String(Number(rate.dailyRateEur)),
      effectiveFrom: rate.effectiveFrom.split('T')[0],
      effectiveTo: rate.effectiveTo ? rate.effectiveTo.split('T')[0] : '',
      clientSegment: rate.clientSegment ?? '',
    });
    setError('');
    setModalOpen(true);
  }

  function handleSave() {
    setError('');
    const daily = parseFloat(form.dailyRateEur);
    if (!form.role) return setError('Rolle ist Pflichtfeld');
    if (isNaN(daily) || daily < 0) return setError('Ungültiger Tagessatz');
    if (!form.effectiveFrom) return setError('Gültig-ab ist Pflichtfeld');
    if (form.effectiveTo && form.effectiveTo <= form.effectiveFrom) {
      return setError('Gültig-bis muss nach Gültig-ab liegen');
    }

    const segment = form.clientSegment || undefined;

    if (editingId) {
      const payload: UpdateRoleRatePayload = {
        dailyRateEur: daily,
        effectiveFrom: form.effectiveFrom,
        effectiveTo: form.effectiveTo || null,
        clientSegment: (segment as any) ?? null,
      };
      updateMutation.mutate({ id: editingId, data: payload }, {
        onSuccess: () => setModalOpen(false),
        onError: (e: any) => setError(e?.response?.data?.message ?? 'Fehler beim Speichern'),
      });
    } else {
      const payload: CreateRoleRatePayload = {
        role: form.role,
        dailyRateEur: daily,
        effectiveFrom: form.effectiveFrom,
        ...(form.effectiveTo && { effectiveTo: form.effectiveTo }),
        ...(segment && { clientSegment: segment as any }),
      };
      createMutation.mutate(payload, {
        onSuccess: () => setModalOpen(false),
        onError: (e: any) => setError(e?.response?.data?.message ?? 'Fehler beim Anlegen'),
      });
    }
  }

  function handleDelete() {
    if (!deleteId) return;
    deleteMutation.mutate(deleteId, {
      onSuccess: () => setDeleteId(null),
    });
  }

  function getRoleLabel(key: string): string {
    return availableRoles?.find((r) => r.key === key)?.label ?? key;
  }

  const isSaving = createMutation.isPending || updateMutation.isPending;

  // Gruppiert: zuerst aktive, dann abgelaufene
  const active = rates.filter((r) => !isExpired(r));
  const expired = rates.filter((r) => isExpired(r));

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
        <div>
          <h2 style={{ fontSize: '1rem', fontWeight: 600, margin: 0, color: 'var(--color-text)' }}>Tagessätze</h2>
          <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-muted)', margin: '0.25rem 0 0' }}>
            1 PT = 8 Std. · Stundensatz = Tagessatz ÷ 8
          </p>
        </div>
        <button style={btnStyle('primary')} onClick={openCreate}>+ Neuer Tagessatz</button>
      </div>

      {isLoading && <p style={{ color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>Laden…</p>}

      {/* Aktive Tagessätze */}
      {active.length > 0 && (
        <div style={{ background: 'var(--color-bg-card)', border: '1px solid var(--color-border)', borderRadius: '0.5rem', overflow: 'hidden', marginBottom: '1.5rem' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
                <th style={thStyle}>Rolle</th>
                <th style={thStyle}>Tagessatz (netto)</th>
                <th style={thStyle}>Stundensatz</th>
                <th style={thStyle}>Gültig ab</th>
                <th style={thStyle}>Gültig bis</th>
                <th style={thStyle}>Segment</th>
                <th style={thStyle}></th>
              </tr>
            </thead>
            <tbody>
              {active.map((rate) => (
                <tr key={rate.id} style={{ borderBottom: '1px solid var(--color-border)' }}>
                  <td style={{ ...tdStyle, fontWeight: 500 }}>{getRoleLabel(rate.role)}</td>
                  <td style={tdStyle}>{fmtRate(rate.dailyRateEur)}</td>
                  <td style={{ ...tdStyle, color: 'var(--color-text-muted)' }}>
                    {(Number(rate.dailyRateEur) / 8).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}/h
                  </td>
                  <td style={tdStyle}>{fmtDate(rate.effectiveFrom)}</td>
                  <td style={tdStyle}>{rate.effectiveTo ? fmtDate(rate.effectiveTo) : <span style={{ color: 'var(--color-text-muted)' }}>unbefristet</span>}</td>
                  <td style={tdStyle}>{rate.clientSegment ? SEGMENT_LABELS[rate.clientSegment] : <span style={{ color: 'var(--color-text-muted)' }}>—</span>}</td>
                  <td style={{ ...tdStyle, display: 'flex', gap: '0.375rem' }}>
                    <button style={btnStyle('default')} onClick={() => openEdit(rate)}>Bearbeiten</button>
                    <button style={btnStyle('danger')} onClick={() => setDeleteId(rate.id)}>Löschen</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!isLoading && active.length === 0 && (
        <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--color-text-muted)', fontSize: '0.875rem', border: '1px dashed var(--color-border)', borderRadius: '0.5rem', marginBottom: '1.5rem' }}>
          Noch keine Tagessätze angelegt.
        </div>
      )}

      {/* Abgelaufene Tagessätze */}
      {expired.length > 0 && (
        <details style={{ marginTop: '0.5rem' }}>
          <summary style={{ fontSize: '0.8125rem', color: 'var(--color-text-muted)', cursor: 'pointer', userSelect: 'none', marginBottom: '0.75rem' }}>
            Abgelaufene Tagessätze ({expired.length})
          </summary>
          <div style={{ background: 'var(--color-bg-card)', border: '1px solid var(--color-border)', borderRadius: '0.5rem', overflow: 'hidden', opacity: 0.7 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
                  <th style={thStyle}>Rolle</th>
                  <th style={thStyle}>Tagessatz (netto)</th>
                  <th style={thStyle}>Gültig ab</th>
                  <th style={thStyle}>Gültig bis</th>
                  <th style={thStyle}>Segment</th>
                  <th style={thStyle}></th>
                </tr>
              </thead>
              <tbody>
                {expired.map((rate) => (
                  <tr key={rate.id} style={{ borderBottom: '1px solid var(--color-border)' }}>
                    <td style={{ ...tdStyle, fontWeight: 500 }}>{getRoleLabel(rate.role)}</td>
                    <td style={tdStyle}>{fmtRate(rate.dailyRateEur)}</td>
                    <td style={tdStyle}>{fmtDate(rate.effectiveFrom)}</td>
                    <td style={tdStyle}>{fmtDate(rate.effectiveTo)}</td>
                    <td style={tdStyle}>{rate.clientSegment ? SEGMENT_LABELS[rate.clientSegment] : '—'}</td>
                    <td style={tdStyle}>
                      <button style={btnStyle('danger')} onClick={() => setDeleteId(rate.id)}>Löschen</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}

      {/* Anlegen / Bearbeiten Modal */}
      {modalOpen && (
        <Modal isOpen title={editingId ? 'Tagessatz bearbeiten' : 'Neuer Tagessatz'} onClose={() => setModalOpen(false)} maxWidth="520px">
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {error && (
              <div style={{ padding: '0.625rem 0.875rem', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '0.375rem', color: '#dc2626', fontSize: '0.8125rem' }}>
                {error}
              </div>
            )}

            {/* Rolle (nur bei Anlegen) */}
            {!editingId && (
              <div>
                <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: 500, marginBottom: '0.375rem', color: 'var(--color-text)' }}>
                  Rolle *
                </label>
                {availableRoles ? (
                  <select
                    value={form.role}
                    onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
                    style={{ width: '100%', padding: '0.5rem 0.75rem', borderRadius: '0.375rem', border: '1px solid var(--color-border)', background: 'var(--color-bg)', color: 'var(--color-text)', fontSize: '0.875rem' }}
                  >
                    <option value="">Bitte wählen…</option>
                    {availableRoles.map((r) => (
                      <option key={r.key} value={r.key}>{r.label} ({r.key})</option>
                    ))}
                  </select>
                ) : (
                  <input
                    value={form.role}
                    onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
                    placeholder="z.B. senior"
                    style={{ width: '100%', padding: '0.5rem 0.75rem', borderRadius: '0.375rem', border: '1px solid var(--color-border)', background: 'var(--color-bg)', color: 'var(--color-text)', fontSize: '0.875rem', boxSizing: 'border-box' }}
                  />
                )}
              </div>
            )}

            {/* Tagessatz */}
            <div>
              <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: 500, marginBottom: '0.375rem', color: 'var(--color-text)' }}>
                Tagessatz (€ netto / PT) *
              </label>
              <input
                type="number"
                min={0}
                step={10}
                value={form.dailyRateEur}
                onChange={(e) => setForm((f) => ({ ...f, dailyRateEur: e.target.value }))}
                placeholder="z.B. 1400"
                style={{ width: '100%', padding: '0.5rem 0.75rem', borderRadius: '0.375rem', border: '1px solid var(--color-border)', background: 'var(--color-bg)', color: 'var(--color-text)', fontSize: '0.875rem', boxSizing: 'border-box' }}
              />
              {form.dailyRateEur && !isNaN(Number(form.dailyRateEur)) && (
                <p style={{ margin: '0.25rem 0 0', fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>
                  = {(Number(form.dailyRateEur) / 8).toLocaleString('de-DE', { minimumFractionDigits: 2 })} €/h
                </p>
              )}
            </div>

            {/* Gültigkeitszeitraum */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: 500, marginBottom: '0.375rem', color: 'var(--color-text)' }}>
                  Gültig ab *
                </label>
                <input
                  type="date"
                  value={form.effectiveFrom}
                  onChange={(e) => setForm((f) => ({ ...f, effectiveFrom: e.target.value }))}
                  style={{ width: '100%', padding: '0.5rem 0.75rem', borderRadius: '0.375rem', border: '1px solid var(--color-border)', background: 'var(--color-bg)', color: 'var(--color-text)', fontSize: '0.875rem', boxSizing: 'border-box' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: 500, marginBottom: '0.375rem', color: 'var(--color-text)' }}>
                  Gültig bis <span style={{ color: 'var(--color-text-muted)', fontWeight: 400 }}>(leer = unbefristet)</span>
                </label>
                <input
                  type="date"
                  value={form.effectiveTo}
                  onChange={(e) => setForm((f) => ({ ...f, effectiveTo: e.target.value }))}
                  style={{ width: '100%', padding: '0.5rem 0.75rem', borderRadius: '0.375rem', border: '1px solid var(--color-border)', background: 'var(--color-bg)', color: 'var(--color-text)', fontSize: '0.875rem', boxSizing: 'border-box' }}
                />
              </div>
            </div>

            {/* Kunden-Segment */}
            <div>
              <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: 500, marginBottom: '0.375rem', color: 'var(--color-text)' }}>
                Kunden-Segment <span style={{ color: 'var(--color-text-muted)', fontWeight: 400 }}>(optional)</span>
              </label>
              <select
                value={form.clientSegment}
                onChange={(e) => setForm((f) => ({ ...f, clientSegment: e.target.value }))}
                style={{ width: '100%', padding: '0.5rem 0.75rem', borderRadius: '0.375rem', border: '1px solid var(--color-border)', background: 'var(--color-bg)', color: 'var(--color-text)', fontSize: '0.875rem' }}
              >
                <option value="">Alle Segmente</option>
                <option value="standard">Standard</option>
                <option value="enterprise">Enterprise</option>
                <option value="public_sector">Öffentlicher Sektor</option>
              </select>
            </div>

            {/* Aktionen */}
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '0.5rem' }}>
              <button style={btnStyle('default')} onClick={() => setModalOpen(false)}>Abbrechen</button>
              <button style={btnStyle('primary')} onClick={handleSave} disabled={isSaving}>
                {isSaving ? 'Speichern…' : 'Speichern'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Löschen-Bestätigung */}
      {deleteId && (
        <Modal isOpen title="Tagessatz löschen" onClose={() => setDeleteId(null)} maxWidth="400px">
          <p style={{ fontSize: '0.875rem', color: 'var(--color-text)', marginBottom: '1.25rem' }}>
            Diesen Tagessatz wirklich löschen? Die Aktion kann nicht rückgängig gemacht werden.
            Bestehende Kalkulationen sind nicht betroffen (Snapshot-Prinzip).
          </p>
          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
            <button style={btnStyle('default')} onClick={() => setDeleteId(null)}>Abbrechen</button>
            <button style={btnStyle('danger')} onClick={handleDelete} disabled={deleteMutation.isPending}>
              {deleteMutation.isPending ? 'Löschen…' : 'Löschen'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
